import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getSubscriptionStatus, SubscriptionType, SubscriptionResponse } from '../../services/subscriptionService';
import { handlePaymentWithCurrency } from '../../utils/paymentUtils';
import { PLAN_PRICING } from '../../services/SubscriptionPrices';
import BillingPeriodToggle from '../ui/BillingPeriodToggle';
import CurrencyModal from '../ui/CurrencyModal';
import PricingCards from '../ui/PricingCards';

const Subscription: React.FC = () => {
  const { currentUser } = useAuth();
  const [subscriptionData, setSubscriptionData] = useState<SubscriptionResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [paymentLoading, setPaymentLoading] = useState<'Plus' | 'Pro' | 'Topup' | null>(null);

  // Modal state for currency selection
  const [showCurrencyModal, setShowCurrencyModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<null | ("Plus" | "Pro" | "Topup")>(null);
  const [billingPeriod, setBillingPeriod] = useState('4month');

  const fetchSubscriptionStatus = useCallback(async () => {
    if (!currentUser?.uid) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const response = await getSubscriptionStatus(currentUser.uid);
      setSubscriptionData(response);

      // Set default billing period based on current subscription plan code
      if (response && response.type !== SubscriptionType.FREE) {
        const currentBillingPeriod = getBillingPeriodFromPlanCode(response.subscriptionCode);
        if (currentBillingPeriod) {
          setBillingPeriod(currentBillingPeriod);
        }
      }
    } catch (error) {
      console.error('Failed to fetch subscription status:', error);
      setError('Failed to load subscription details. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [currentUser]);

  // Function to determine billing period from plan code
  const getBillingPeriodFromPlanCode = (planCode: number): string | null => {
    switch (planCode) {
      // Monthly plans (original codes: 102=pro, 103=plus, 104=enterprise)
      case SubscriptionType.SUBSCRIPTION_PRO_CODE:
      case SubscriptionType.SUBSCRIPTION_PLUS_CODE:
      case SubscriptionType.SUBSCRIPTION_ENTERPRISE_CODE:
        return 'monthly';

      // 4-month plans (codes: 402=pro, 403=plus, 404=enterprise)
      case SubscriptionType.SUBSCRIPTION_4MONTH_PRO_CODE:
      case SubscriptionType.SUBSCRIPTION_4MONTH_PLUS_CODE:
      case SubscriptionType.SUBSCRIPTION_4MONTH_ENTERPRISE_CODE:
        return '4month';

      // 12-month/yearly plans (codes: 1202=pro, 1203=plus, 1204=enterprise)
      case SubscriptionType.SUBSCRIPTION_12MONTH_PRO_CODE:
      case SubscriptionType.SUBSCRIPTION_12MONTH_PLUS_CODE:
      case SubscriptionType.SUBSCRIPTION_12MONTH_ENTERPRISE_CODE:
        return 'yearly';

      // Free plan or unknown
      case SubscriptionType.SUBSCRIPTION_FREE_CODE:
      default:
        return null;
    }
  };

  // Function to get plan type from plan code
  const getPlanTypeFromPlanCode = (planCode: number): string | null => {
    switch (planCode) {
      // Pro plans (all durations: 102, 402, 1202)
      case SubscriptionType.SUBSCRIPTION_PRO_CODE:
      case SubscriptionType.SUBSCRIPTION_4MONTH_PRO_CODE:
      case SubscriptionType.SUBSCRIPTION_12MONTH_PRO_CODE:
        return 'pro';

      // Plus plans (all durations: 103, 403, 1203)
      case SubscriptionType.SUBSCRIPTION_PLUS_CODE:
      case SubscriptionType.SUBSCRIPTION_4MONTH_PLUS_CODE:
      case SubscriptionType.SUBSCRIPTION_12MONTH_PLUS_CODE:
        return 'plus';

      // Enterprise plans (all durations: 104, 404, 1204)
      case SubscriptionType.SUBSCRIPTION_ENTERPRISE_CODE:
      case SubscriptionType.SUBSCRIPTION_4MONTH_ENTERPRISE_CODE:
      case SubscriptionType.SUBSCRIPTION_12MONTH_ENTERPRISE_CODE:
        return 'enterprise';

      // Free plan (101)
      case SubscriptionType.SUBSCRIPTION_FREE_CODE:
        return 'free';

      default:
        return null;
    }
  };

  useEffect(() => {
    fetchSubscriptionStatus();
  }, [currentUser, fetchSubscriptionStatus]);

  // Currency selection logic using shared payment utility
  const handleCurrencySelect = async (currency: 'USD' | 'INR') => {
    if (!selectedPlan || !currentUser?.uid) return;
    setShowCurrencyModal(false);
    try {
      setPaymentLoading(selectedPlan);

      const paymentLink = await handlePaymentWithCurrency(currency, {
        plan: selectedPlan,
        billingPeriod,
        userId: currentUser.uid
      });

      window.open(paymentLink, '_blank');
    } catch (error) {
      console.error('Payment error:', error);
      setError('Failed to generate payment link. Please try again.');
    } finally {
      setPaymentLoading(null);
      setSelectedPlan(null);
    }
  };

  // Show modal for paid plans
  const handleUpgradeWithCurrency = (plan: "Plus" | "Pro") => {
    setSelectedPlan(plan);
    setShowCurrencyModal(true);
  };
  const handleTopupWithCurrency = () => {
    setSelectedPlan('Topup');
    setShowCurrencyModal(true);
  };

  const handleCancelSubscription = () => {
    if (!currentUser || !subscriptionData) return;

    const emailBody = `Hello,

I would like to cancel my subscription. Please find my details below:

User Information:
- User ID: ${currentUser.uid}
- Email: ${currentUser.email || 'Not provided'}
- Name: ${currentUser.displayName || 'Not provided'}

Current Subscription Details:
- Plan Type: ${subscriptionData.endingTimestamp && isExpired(subscriptionData.endingTimestamp) && subscriptionData.previous_type
        ? getSubscriptionDisplayName(subscriptionData.previous_type, subscriptionData.previous_subscriptionCode) + ' (Expired)'
        : getSubscriptionDisplayName(subscriptionData.type, subscriptionData.subscriptionCode)}
- Plan Code: ${subscriptionData.endingTimestamp && isExpired(subscriptionData.endingTimestamp) && subscriptionData.previous_subscriptionCode
        ? subscriptionData.previous_subscriptionCode
        : subscriptionData.subscriptionCode || 'N/A'}
- Subscription ID: ${subscriptionData.subscriptionId || 'N/A'}
${subscriptionData.endingTimestamp ? `- Expires On: ${new Date(subscriptionData.endingTimestamp).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}` : ''}
${subscriptionData.transactionTimestamp ? `- Last Updated: ${new Date(subscriptionData.transactionTimestamp).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}` : ''}

Reason for Cancellation:
[Please provide your reason here]

Additional Comments:
[Any additional information you'd like to share]

Please confirm the cancellation and let me know if you need any additional information.

Thank you for your assistance.

Best regards,
${currentUser.displayName || currentUser.email || 'User'}`;

    const subject = 'Cancel Subscription';
    const mailtoLink = `mailto:manish.sahu@snapmydesign.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(emailBody)}`;

    window.open(mailtoLink, '_blank');
  };

  // Handler for PricingCards component
  const handlePlanAction = (plan: 'Free' | 'Plus' | 'Pro' | 'Topup') => {
    if (plan === 'Plus') {
      handleUpgradeWithCurrency('Plus');
    } else if (plan === 'Pro') {
      handleUpgradeWithCurrency('Pro');
    } else if (plan === 'Topup') {
      handleTopupWithCurrency();
    }
    // Free plan doesn't need an action
  };

  const getSubscriptionDisplayName = (type: string, subscriptionCode?: number) => {
    // If we have a subscription code, use it for more accurate plan type detection
    if (subscriptionCode) {
      const planType = getPlanTypeFromPlanCode(subscriptionCode);

      if (planType) {
        const planName = planType.charAt(0).toUpperCase() + planType.slice(1);
        return `${planName} Plan`;
      }
    }

    if (!type) return 'Free Plan';

    // Handle new API format like "plus_4months", "pro_yearly", etc.
    const planType = type.split('_')[0]; // Extract part before underscore

    // Fallback to basic type-based naming
    switch (planType.toLowerCase()) {
      case 'pro':
        return 'Pro Plan';
      case 'plus':
        return 'Plus Plan';
      case 'enterprise':
        return 'Enterprise Plan';
      case 'free':
      default:
        return 'Free Plan';
    }
  }; const isExpiringSoon = (endingTimestamp?: string) => {
    if (!endingTimestamp) return false;
    const expirationDate = new Date(endingTimestamp);
    const today = new Date();
    const diffTime = expirationDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 7 && diffDays > 0;
  };

  const isExpired = (endingTimestamp?: string) => {
    if (!endingTimestamp) return false;
    const expirationDate = new Date(endingTimestamp);
    const today = new Date();
    return expirationDate < today;
  };

  // Function to check if a plan card should be marked as "Current Plan"
  const isCurrentPlan = (planType: string): boolean => {
    if (!subscriptionData) {
      return planType === 'free';
    }

    // For free plan
    if (subscriptionData.type === SubscriptionType.FREE) {
      return planType === 'free';
    }

    // Check if subscription is expired - expired plans should not be highlighted
    if (subscriptionData.endingTimestamp && isExpired(subscriptionData.endingTimestamp)) {
      return false; // No plan should be highlighted if subscription is expired
    }

    // For paid plans, check both plan type and billing period using plan code
    const currentBillingPeriod = getBillingPeriodFromPlanCode(subscriptionData.subscriptionCode);
    const currentPlanType = getPlanTypeFromPlanCode(subscriptionData.subscriptionCode);

    // Topup is never the "current plan"
    if (planType === 'topup') {
      return false;
    }

    // Check if both plan type and billing period match
    return planType === currentPlanType && currentBillingPeriod === billingPeriod;
  };

  // Helper function to check if current subscription is a Free plan
  const isFreePlan = (): boolean => {
    if (!subscriptionData) return true;
    return subscriptionData.type === SubscriptionType.FREE;
  };

  const getStatusColor = () => {
    if (!subscriptionData?.endingTimestamp) return 'text-green-600';
    if (isExpired(subscriptionData.endingTimestamp)) return 'text-red-600';
    if (isExpiringSoon(subscriptionData.endingTimestamp)) return 'text-yellow-600';
    return 'text-green-600';
  };

  const getStatusText = () => {
    if (!subscriptionData?.endingTimestamp) return 'Active';
    if (isExpired(subscriptionData.endingTimestamp)) return 'Expired';
    if (isExpiringSoon(subscriptionData.endingTimestamp)) return 'Expiring Soon';
    return 'Active';
  };

  if (!currentUser) {
    return (
      <div className="p-3 md:p-6 max-w-7xl mx-auto">
        <div className="text-center py-12">
          <i className="fas fa-user-lock text-6xl text-text-description mb-4"></i>
          <h2 className="text-2xl font-bold text-text-description mb-2">Please Login</h2>
          <p className="text-text-description mb-6">You need to be logged in to view your subscription details.</p>
          <Link
            to="/login"
            className="bg-secondary-500 text-white px-6 py-3 rounded-lg hover:bg-secondary-600 transition-colors"
          >
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm p-4 md:p-6">
      <CurrencyModal
        showModal={showCurrencyModal}
        isLoading={paymentLoading !== null}
        onCurrencySelect={handleCurrencySelect}
        onClose={() => setShowCurrencyModal(false)}
      />
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={fetchSubscriptionStatus}
          className="bg-accent-sand hover:bg-accent-taupe text-text-description px-3 py-2 rounded-lg transition-colors flex items-center space-x-2 text-sm"
        >
          <i className="fas fa-refresh text-xs"></i>
          <span>Refresh</span>
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          <div className="flex items-center space-x-2">
            <i className="fas fa-exclamation-circle"></i>
            <span>{error}</span>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-secondary-500 mx-auto mb-3"></div>
          <p className="text-text-description text-sm">Loading subscription details...</p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Current Subscription Card */}
          <div className="bg-accent-sand rounded-lg p-4 border">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-secondary-100 rounded-full flex items-center justify-center">
                  <i className="fas fa-crown text-secondary-600 text-lg"></i>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-text-heading">
                    {subscriptionData && subscriptionData.endingTimestamp && isExpired(subscriptionData.endingTimestamp) && subscriptionData.previous_type
                      ? getSubscriptionDisplayName(subscriptionData.previous_type, subscriptionData.previous_subscriptionCode)
                      : subscriptionData ? getSubscriptionDisplayName(subscriptionData.type, subscriptionData.subscriptionCode) : 'Free Plan'}
                  </h2>
                  <p className={`text-sm font-medium ${getStatusColor()}`}>
                    {subscriptionData ? getStatusText() : 'Active'}
                  </p>

                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-text-description">Plan Code</p>
                <p className="font-mono text-sm text-text-heading">
                  {subscriptionData && subscriptionData.endingTimestamp && isExpired(subscriptionData.endingTimestamp) && subscriptionData.previous_subscriptionCode
                    ? subscriptionData.previous_subscriptionCode
                    : subscriptionData?.subscriptionCode || 101}
                </p>
              </div>
            </div>

            {subscriptionData && subscriptionData.type !== SubscriptionType.FREE && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                <div className="bg-white rounded-md p-3">
                  <p className="text-xs text-text-description mb-1">Subscription ID</p>
                  <p className="font-mono text-sm text-text-heading">
                    {subscriptionData.subscriptionId ?
                      `${subscriptionData.subscriptionId.slice(0, 8)}...` :
                      'N/A'
                    }
                  </p>
                </div>
                {subscriptionData.endingTimestamp && (
                  <div className="bg-white rounded-md p-3">
                    <p className="text-xs text-text-description mb-1">Expires On</p>
                    <p className="text-sm text-text-heading">
                      {new Date(subscriptionData.endingTimestamp).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                )}
                {subscriptionData.transactionTimestamp && (
                  <div className="bg-white rounded-md p-3">
                    <p className="text-xs text-text-description mb-1">Last Updated</p>
                    <p className="text-sm text-text-heading">
                      {new Date(subscriptionData.transactionTimestamp).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Expiration Warnings */}
            {subscriptionData?.endingTimestamp && isExpired(subscriptionData.endingTimestamp) && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <div className="flex items-center space-x-2 mb-1">
                  <i className="fas fa-exclamation-triangle text-red-500 text-sm"></i>
                  <h3 className="font-medium text-red-800 text-sm">Subscription Expired</h3>
                </div>
                <p className="text-red-700 text-xs">
                  Your {subscriptionData.previous_type ? getSubscriptionDisplayName(subscriptionData.previous_type, subscriptionData.previous_subscriptionCode) : 'subscription'} expired on {new Date(subscriptionData.endingTimestamp).toLocaleDateString()}.
                  Renew now to continue using premium features.
                </p>
              </div>
            )}

            {subscriptionData?.endingTimestamp && isExpiringSoon(subscriptionData.endingTimestamp) && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <div className="flex items-center space-x-2 mb-1">
                  <i className="fas fa-hourglass-half text-yellow-500 text-sm"></i>
                  <h3 className="font-medium text-yellow-800 text-sm">Subscription Expiring Soon</h3>
                </div>
                <p className="text-yellow-700 text-xs">
                  Your subscription expires in {Math.ceil((new Date(subscriptionData.endingTimestamp).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))} days.
                  Renew now to avoid service interruption.
                </p>
              </div>
            )}
          </div>

          {/* Available Plans - Hide for Pro users */}
          {subscriptionData?.type && (
            <div className="bg-accent-sand rounded-lg p-4 border">
              <h3 className="text-xl font-bold text-text-heading mb-4">Available Plans</h3>

              {/* Billing Period Toggle */}
              <div className="mb-6 flex justify-center">
                <div className="relative bg-accent-sand rounded-lg p-1 flex">
                  <button
                    onClick={() => setBillingPeriod('monthly')}
                    className={`relative px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${billingPeriod === 'monthly'
                      ? 'bg-white text-secondary-600 shadow-sm'
                      : 'text-text-description hover:text-text-heading'
                      }`}
                  >
                    Solo
                  </button>
                  <button
                    onClick={() => setBillingPeriod('4month')}
                    className={`relative px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${billingPeriod === '4month'
                      ? 'bg-white text-secondary-600 shadow-sm'
                      : 'text-text-description hover:text-text-heading'
                      }`}
                  >
                    Team
                    <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      15% OFF
                    </span>
                  </button>
                  <button
                    onClick={() => setBillingPeriod('yearly')}
                    className={`relative px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${billingPeriod === 'yearly'
                      ? 'bg-white text-secondary-600 shadow-sm'
                      : 'text-text-description hover:text-text-heading'
                      }`}
                  >
                    Organization
                    <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-secondary-100 text-secondary-800">
                      20% OFF
                    </span>
                  </button>
                </div>
              </div>

              <div className="mb-4 p-3 bg-secondary-100 border border-secondary-200 rounded-md">
                <div className="flex items-center space-x-2 mb-1">
                  <i className="fas fa-info-circle text-secondary-600 text-sm"></i>
                  <h4 className="font-medium text-secondary-800 text-sm">Currency Information</h4>
                </div>
                <p className="text-secondary-700 text-xs">
                  INR (&#8377;) pricing is for Indian customers and USD ($) pricing is for International customers.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* Free Plan */}
                <div className={`border rounded-lg p-6 flex flex-col h-full ${isCurrentPlan('free') ? 'border-secondary-500 bg-secondary-100' : 'border-accent-taupe'}`}>
                  <div>
                    <h4 className="text-lg font-semibold mb-2">Free Plan</h4>
                    <div className="mb-4">
                      <span className="text-3xl font-bold text-text-heading">$0</span>
                      <span className="text-base font-medium text-text-description">/forever</span>
                    </div>
                    <p className="text-text-description text-sm mb-4">Basic features for getting started</p>
                  </div>
                  <ul className="space-y-2 text-sm text-text-description mb-6 flex-grow">
                    <li className="flex items-center space-x-2">
                      <svg className="flex-shrink-0 h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Basic AI Photoshoot (5 credits)</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <svg className="flex-shrink-0 h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Standard Try-On</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <svg className="flex-shrink-0 h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Basic Gallery Storage (10 images)</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <svg className="flex-shrink-0 h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Community Support</span>
                    </li>
                  </ul>
                  <div className="mt-auto">
                    {isCurrentPlan('free') ? (
                      <button className="w-full bg-secondary-500 text-white py-3 px-4 rounded-lg cursor-default font-medium">
                        Current Plan
                      </button>
                    ) : (
                      <button
                        disabled
                        className="w-full bg-gray-600 text-white py-3 px-4 rounded-lg transition-colors font-medium cursor-not-allowed"
                      >
                        Free Forever
                      </button>
                    )}
                  </div>
                </div>



                {/* Plus Plan */}
                <div className={`border rounded-lg p-6 flex flex-col h-full ${isCurrentPlan('plus') ? 'border-secondary-500 bg-secondary-100' : 'border-accent-taupe'}`}>
                  <div>
                    <h4 className="text-lg font-semibold mb-2">Plus Plan</h4>
                    <div className="mb-4">
                      <div className="text-3xl font-bold text-text-heading">
                        ${billingPeriod === 'monthly'
                          ? PLAN_PRICING.plus_monthly.usd
                          : billingPeriod === '4month'
                            ? PLAN_PRICING.plus_4months.usd
                            : PLAN_PRICING.plus_yearly.usd} or &#8377;{billingPeriod === 'monthly'
                              ? PLAN_PRICING.plus_monthly.inr
                              : billingPeriod === '4month'
                                ? PLAN_PRICING.plus_4months.inr
                                : PLAN_PRICING.plus_yearly.inr}
                      </div>
                      <span className="text-base font-medium text-text-description">
                        /{billingPeriod === 'monthly' ? 'per month' : billingPeriod === '4month' ? '4 months' : 'year'}
                      </span>

                      {/* Original Price and Savings */}
                      {billingPeriod !== 'monthly' && (
                        <>
                          <p className="text-sm text-text-description line-through mt-1">
                            ${billingPeriod === '4month'
                              ? PLAN_PRICING.plus_monthly.usd * 4
                              : PLAN_PRICING.plus_monthly.usd * 12} or &#8377;{billingPeriod === '4month'
                                ? PLAN_PRICING.plus_monthly.inr * 4
                                : PLAN_PRICING.plus_monthly.inr * 12}
                          </p>
                          <p className={`text-sm font-medium mt-1 ${billingPeriod === '4month' ? 'text-green-600' : 'text-secondary-600'
                            }`}>
                            Save {billingPeriod === '4month' ? '15%' : '20%'}
                          </p>
                        </>
                      )}
                    </div>
                    <p className="text-text-description text-sm mb-4">Perfect for regular users</p>
                  </div>
                  <ul className="space-y-2 text-sm text-text-description mb-6 flex-grow">
                    <li className="flex items-center space-x-2">
                      <svg className="flex-shrink-0 h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Advanced AI Photoshoot ({billingPeriod === 'monthly'
                        ? `${PLAN_PRICING.plus_monthly.credits} credits/month`
                        : billingPeriod === '4month'
                          ? `${PLAN_PRICING.plus_4months.credits} credits total`
                          : `${PLAN_PRICING.plus_yearly.credits} credits total`})</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <svg className="flex-shrink-0 h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Enhanced Try-On</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <svg className="flex-shrink-0 h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Extended Gallery Storage (100 images)</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <svg className="flex-shrink-0 h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Priority Support</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <svg className="flex-shrink-0 h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Outfit Extractor (Flatlay)</span>
                    </li>
                  </ul>
                  <div className="mt-auto">
                    <button
                      onClick={() => handleUpgradeWithCurrency('Plus')}
                      disabled={paymentLoading === 'Plus'}
                      className="w-full bg-secondary-500 text-white py-3 px-4 rounded-lg hover:bg-secondary-600 transition-colors disabled:opacity-50 flex items-center justify-center space-x-2 font-medium"
                    >
                      {paymentLoading === 'Plus' ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          <span>Processing...</span>
                        </>
                      ) : (
                        <span>{isCurrentPlan('plus') ? 'Renew Plan' : 'Upgrade to Plus'}</span>
                      )}
                    </button>
                  </div>
                </div>

                {/* Pro Plan */}
                <div className={`border rounded-lg p-6 flex flex-col h-full ${isCurrentPlan('pro') ? 'border-secondary-500 bg-secondary-100' : 'border-accent-taupe'} relative`}>
                  {!isCurrentPlan('pro') && (
                    <div className="absolute top-0 right-0 bg-secondary-1000 text-white text-xs font-bold px-3 py-1 rounded-bl-lg rounded-tr-lg">
                      MOST POPULAR
                    </div>
                  )}
                  <div>
                    <h4 className="text-lg font-semibold mb-2">Pro Plan</h4>
                    <div className="mb-4">
                      <div className="text-3xl font-bold text-text-heading">
                        ${billingPeriod === 'monthly'
                          ? PLAN_PRICING.pro_monthly.usd
                          : billingPeriod === '4month'
                            ? PLAN_PRICING.pro_4months.usd
                            : PLAN_PRICING.pro_yearly.usd} or &#8377;{billingPeriod === 'monthly'
                              ? PLAN_PRICING.pro_monthly.inr
                              : billingPeriod === '4month'
                                ? PLAN_PRICING.pro_4months.inr
                                : PLAN_PRICING.pro_yearly.inr}
                      </div>
                      <span className="text-base font-medium text-text-description">
                        /{billingPeriod === 'monthly' ? 'per month' : billingPeriod === '4month' ? '4 months' : 'year'}
                      </span>

                      {/* Original Price and Savings */}
                      {billingPeriod !== 'monthly' && (
                        <>
                          <p className="text-sm text-text-description line-through mt-1">
                            ${billingPeriod === '4month'
                              ? PLAN_PRICING.pro_monthly.usd * 4
                              : PLAN_PRICING.pro_monthly.usd * 12} or &#8377;{billingPeriod === '4month'
                                ? PLAN_PRICING.pro_monthly.inr * 4
                                : PLAN_PRICING.pro_monthly.inr * 12}
                          </p>
                          <p className={`text-sm font-medium mt-1 ${billingPeriod === '4month' ? 'text-green-600' : 'text-secondary-600'
                            }`}>
                            Save {billingPeriod === '4month' ? '15%' : '20%'}
                          </p>
                        </>
                      )}
                    </div>
                    <p className="text-text-description text-sm mb-4">For power users and professionals</p>
                  </div>
                  <ul className="space-y-2 text-sm text-text-description mb-6 flex-grow">
                    <li className="flex items-center space-x-2">
                      <svg className="flex-shrink-0 h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Advanced AI Photoshoot ({billingPeriod === 'monthly'
                        ? `${PLAN_PRICING.pro_monthly.credits} credits/month`
                        : billingPeriod === '4month'
                          ? `${PLAN_PRICING.pro_4months.credits} credits total`
                          : `${PLAN_PRICING.pro_yearly.credits} credits total`})</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <svg className="flex-shrink-0 h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Premium Try-On</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <svg className="flex-shrink-0 h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Canvas Editor & Video Generation</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <svg className="flex-shrink-0 h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Unlimited Gallery Storage</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <svg className="flex-shrink-0 h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>24/7 Priority Support</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <svg className="flex-shrink-0 h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Outfit Extractor (Flatlay)</span>
                    </li>
                  </ul>
                  <div className="mt-auto">
                    <button
                      onClick={() => handleUpgradeWithCurrency('Pro')}
                      disabled={paymentLoading === 'Pro'}
                      className="w-full bg-secondary-500 text-white py-3 px-4 rounded-lg hover:bg-secondary-600 transition-colors disabled:opacity-50 flex items-center justify-center space-x-2 font-medium"
                    >
                      {paymentLoading === 'Pro' ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          <span>Processing...</span>
                        </>
                      ) : (
                        <span>{isCurrentPlan('pro') ? 'Renew Plan' : 'Upgrade to Pro'}</span>
                      )}
                    </button>
                  </div>
                </div>

                {/* Topup Plan */}
                <div className="border rounded-lg p-6 flex flex-col h-full border-accent-taupe">
                  <div>
                    <h4 className="text-lg font-semibold mb-2">Credit Topup</h4>
                    <div className="mb-4">
                      <div className="text-3xl font-bold text-text-heading">
                        ${billingPeriod === 'monthly'
                          ? PLAN_PRICING.topup_monthly.usd
                          : billingPeriod === '4month'
                            ? PLAN_PRICING.topup_4months.usd
                            : PLAN_PRICING.topup_yearly.usd} or &#8377;{billingPeriod === 'monthly'
                              ? PLAN_PRICING.topup_monthly.inr
                              : billingPeriod === '4month'
                                ? PLAN_PRICING.topup_4months.inr
                                : PLAN_PRICING.topup_yearly.inr}
                      </div>
                      <span className="text-base font-medium text-text-description">/one-time</span>

                      {/* Original Price and Savings */}
                      {billingPeriod !== 'monthly' && (
                        <>
                          <p className="text-sm text-text-description line-through mt-1">
                            ${billingPeriod === '4month'
                              ? PLAN_PRICING.topup_monthly.usd * 4
                              : PLAN_PRICING.topup_monthly.usd * 12} or &#8377;{billingPeriod === '4month'
                                ? PLAN_PRICING.topup_monthly.inr * 4
                                : PLAN_PRICING.topup_monthly.inr * 12}
                          </p>
                          <p className={`text-sm font-medium mt-1 ${billingPeriod === '4month' ? 'text-green-600' : 'text-secondary-600'
                            }`}>
                            Save {billingPeriod === '4month' ? '15%' : '20%'}
                          </p>
                        </>
                      )}
                    </div>
                    <p className="text-text-description text-sm mb-4">No {billingPeriod === 'monthly' ? 'monthly' : 'subscription'} commitment needed</p>
                  </div>
                  <ul className="space-y-2 text-sm text-text-description mb-6 flex-grow">
                    <li className="flex items-center space-x-2">
                      <svg className="flex-shrink-0 h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>{billingPeriod === 'monthly'
                        ? PLAN_PRICING.topup_monthly.credits
                        : billingPeriod === '4month'
                          ? PLAN_PRICING.topup_4months.credits
                          : PLAN_PRICING.topup_yearly.credits} Credits Instantly</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <svg className="flex-shrink-0 h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>No {billingPeriod === 'monthly' ? 'Monthly' : 'Subscription'} Commitment</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <svg className="flex-shrink-0 h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Credits Never Expire</span>
                    </li>
                    {billingPeriod !== 'monthly' && (
                      <li className="flex items-center space-x-2">
                        <svg className="flex-shrink-0 h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span>Bulk Credit Savings</span>
                      </li>
                    )}
                  </ul>
                  <div className="mt-auto">
                    <button
                      onClick={handleTopupWithCurrency}
                      disabled={paymentLoading === 'Topup'}
                      className="w-full bg-green-600 text-white py-3 px-4 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center justify-center space-x-2 font-medium"
                    >
                      {paymentLoading === 'Topup' ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          <span>Processing...</span>
                        </>
                      ) : (
                        <span>Buy Credits</span>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Additional Actions */}
          <div className="bg-white rounded-xl shadow-sm p-6 border">
            <h3 className="text-xl font-bold text-text-heading mb-6">
              Additional Actions
            </h3>
            <div className={`grid gap-6 ${!isFreePlan()
              ? 'grid-cols-1 lg:grid-cols-3 max-w-6xl mx-auto'
              : 'grid-cols-1 md:grid-cols-2'
              }`}>
              {/* Top-up Credits */}
              <div className="border rounded-lg p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <i className="fas fa-plus-circle text-green-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold">Quick Credit Top-up</h4>
                    <p className="text-sm text-text-description">Add {billingPeriod === 'monthly'
                      ? PLAN_PRICING.topup_monthly.credits
                      : billingPeriod === '4month'
                        ? PLAN_PRICING.topup_4months.credits
                        : PLAN_PRICING.topup_yearly.credits} credits instantly (${billingPeriod === 'monthly'
                          ? PLAN_PRICING.topup_monthly.usd
                          : billingPeriod === '4month'
                            ? PLAN_PRICING.topup_4months.usd
                            : PLAN_PRICING.topup_yearly.usd} or &#8377;{billingPeriod === 'monthly'
                              ? PLAN_PRICING.topup_monthly.inr
                              : billingPeriod === '4month'
                                ? PLAN_PRICING.topup_4months.inr
                                : PLAN_PRICING.topup_yearly.inr}) - No commitment required</p>
                  </div>
                </div>
                <button
                  onClick={() => handleTopupWithCurrency()}
                  disabled={paymentLoading === 'Topup'}
                  className="w-full bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center justify-center space-x-2"
                >
                  {paymentLoading === 'Topup' ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Processing...</span>
                    </>
                  ) : (
                    <span>Add {billingPeriod === 'monthly'
                      ? PLAN_PRICING.topup_monthly.credits
                      : billingPeriod === '4month'
                        ? PLAN_PRICING.topup_4months.credits
                        : PLAN_PRICING.topup_yearly.credits} Credits</span>
                  )}
                </button>
              </div>

              {/* Contact Support */}
              <div className="border rounded-lg p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 bg-secondary-100 rounded-full flex items-center justify-center">
                    <i className="fas fa-headset text-secondary-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold">Contact Support</h4>
                    <p className="text-sm text-text-description">Get help with your subscription</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <button
                    onClick={() => window.open('mailto:manish.sahu@snapmydesign.com?subject=Subscription Support', '_blank')}
                    className="w-full bg-secondary-500 text-white py-2 px-4 rounded-lg hover:bg-secondary-600 transition-colors flex items-center justify-center space-x-2"
                  >
                    <i className="fas fa-envelope"></i>
                    <span>Email Support</span>
                  </button>
                  <button
                    onClick={() => window.open('tel:+919005236891', '_self')}
                    className="w-full bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center space-x-2"
                  >
                    <i className="fas fa-phone"></i>
                    <span>Call: +91-9005236891</span>
                  </button>
                </div>
              </div>

              {/* Cancel Subscription - Show for paid subscribers */}
              {!isFreePlan() && (
                <div className="border rounded-lg p-6 border-red-200">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                      <i className="fas fa-times-circle text-red-600"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-red-800">Cancel Subscription</h4>
                      <p className="text-sm text-text-description">Request cancellation of your plan</p>
                    </div>
                  </div>
                  <button
                    onClick={handleCancelSubscription}
                    className="w-full bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center space-x-2"
                  >
                    <i className="fas fa-ban"></i>
                    <span>Request Cancellation</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Subscription; 